import React, { useEffect, useState } from 'react';
import {
    PlusIcon,
    MagnifyingGlassIcon,
    PencilIcon,
    TrashIcon,
    ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import {
    apiService,
    PaymentProvider,
    PaymentProviderCredentialPayload,
    PaymentProviderPayload,
} from '../services/api';
import ConfirmModal from './ConfirmModal';

interface PaymentProviderManagementProps {
    onBack?: () => void;
}

type ModalMode = 'create' | 'edit';

const PaymentProviderManagement: React.FC<PaymentProviderManagementProps> = ({ onBack }) => {
    const [providers, setProviders] = useState<PaymentProvider[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');

    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState<ModalMode>('create');
    const [selectedProvider, setSelectedProvider] = useState<PaymentProvider | null>(null);
    const [saving, setSaving] = useState(false);
    const [confirmModal, setConfirmModal] = useState<{
        visible: boolean;
        type: 'create' | 'update' | 'delete' | null;
        provider: PaymentProvider | null;
        message: string;
    }>({
        visible: false,
        type: null,
        provider: null,
        message: '',
    });

    const [formState, setFormState] = useState<PaymentProviderPayload>({
        code: '',
        name: '',
        description: '',
        isActive: true,
        credentials: [],
    });

    const fetchProviders = async () => {
        setLoading(true);
        try {
            const response = await apiService.getPaymentProviders({
                page: currentPage,
                limit: pageSize,
                search: searchTerm || undefined,
                isActive: isActiveFilter === 'all' ? undefined : isActiveFilter === 'active',
            });
            setProviders(response.providers || []);
            setTotal(response.pagination?.total || 0);
        } catch (error) {
            console.error('Failed to fetch payment providers:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProviders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, searchTerm, isActiveFilter]);

    const openCreateModal = () => {
        setModalMode('create');
        setSelectedProvider(null);
        setFormState({
            code: '',
            name: '',
            description: '',
            isActive: true,
            credentials: [],
        });
        setModalVisible(true);
    };

    const openEditModal = (provider: PaymentProvider) => {
        setModalMode('edit');
        setSelectedProvider(provider);
        setFormState({
            code: provider.code,
            name: provider.name,
            description: provider.description,
            isActive: provider.isActive,
            credentials: (provider.credentials || []).map<PaymentProviderCredentialPayload>((c) => ({
                name: c.name,
                environment: c.environment,
                merchantId: c.merchantId,
                serverKey: c.serverKey,
                clientKey: c.clientKey,
                additionalConfig: c.additionalConfig || undefined,
                isActive: c.isActive,
            })),
        });
        setModalVisible(true);
    };

    const closeModal = () => {
        setModalVisible(false);
        setSelectedProvider(null);
    };

    const handleFormChange = (field: keyof PaymentProviderPayload, value: any) => {
        setFormState((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleCredentialChange = (
        index: number,
        field: keyof PaymentProviderCredentialPayload,
        value: any,
    ) => {
        setFormState((prev) => {
            const nextCredentials = [...(prev.credentials || [])];
            nextCredentials[index] = {
                ...(nextCredentials[index] || {
                    name: '',
                    environment: 'sandbox',
                    merchantId: '',
                    serverKey: '',
                    clientKey: '',
                }),
                [field]: value,
            };
            return {
                ...prev,
                credentials: nextCredentials,
            };
        });
    };

    const addCredentialRow = () => {
        setFormState((prev) => ({
            ...prev,
            credentials: [
                ...(prev.credentials || []),
                {
                    name: '',
                    environment: 'sandbox',
                    merchantId: '',
                    serverKey: '',
                    clientKey: '',
                    additionalConfig: '',
                    isActive: true,
                },
            ],
        }));
    };

    const removeCredentialRow = (index: number) => {
        setFormState((prev) => {
            const nextCredentials = [...(prev.credentials || [])];
            nextCredentials.splice(index, 1);
            return {
                ...prev,
                credentials: nextCredentials,
            };
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!formState.code.trim() || !formState.name.trim() || !formState.description.trim()) {
            alert('Code, name and description are required');
            return;
        }

        const trimmedCode = formState.code.trim().toLowerCase();
        const payload: PaymentProviderPayload = {
            code: trimmedCode,
            name: formState.name.trim(),
            description: formState.description.trim(),
            isActive: formState.isActive,
            credentials: (formState.credentials || [])
                .map((c) => ({
                    ...c,
                    name: c.name.trim(),
                    environment: c.environment || 'sandbox',
                    merchantId: c.merchantId.trim(),
                    serverKey: c.serverKey.trim(),
                    clientKey: c.clientKey.trim(),
                    additionalConfig: c.additionalConfig?.trim() || undefined,
                }))
                .filter((c) => c.name && c.environment && c.merchantId && c.serverKey && c.clientKey),
        };

        // For create, save directly without confirmation
        if (modalMode === 'create') {
            setSaving(true);
            try {
                await apiService.createPaymentProvider(payload);
                closeModal();
                fetchProviders();
            } catch (error: any) {
                console.error('Failed to save payment provider:', error);
                alert(error.message || 'Failed to save payment provider');
            } finally {
                setSaving(false);
            }
            return;
        } else if (selectedProvider) {
            // Check if there are any changes
            const currentCredentials = (selectedProvider.credentials || []).map(c => ({
                name: c.name,
                environment: c.environment,
                merchantId: c.merchantId,
                serverKey: c.serverKey,
                clientKey: c.clientKey,
                additionalConfig: c.additionalConfig || '',
                isActive: c.isActive,
            }));
            const newCredentials = (formState.credentials || [])
                .map((c) => ({
                    name: c.name.trim(),
                    environment: c.environment || 'sandbox',
                    merchantId: c.merchantId.trim(),
                    serverKey: c.serverKey.trim(),
                    clientKey: c.clientKey.trim(),
                    additionalConfig: (c.additionalConfig?.trim() || ''),
                    isActive: c.isActive,
                }))
                .filter((c) => c.name && c.environment && c.merchantId && c.serverKey && c.clientKey);

            const credentialsChanged = JSON.stringify(currentCredentials.sort((a, b) => a.name.localeCompare(b.name))) !== 
                                     JSON.stringify(newCredentials.sort((a, b) => a.name.localeCompare(b.name)));

            const hasChanges = 
                trimmedCode !== selectedProvider.code ||
                formState.name.trim() !== selectedProvider.name ||
                formState.description.trim() !== selectedProvider.description ||
                formState.isActive !== selectedProvider.isActive ||
                credentialsChanged;

            if (!hasChanges) {
                // No changes, just close the modal
                closeModal();
                return;
            }

            // Show confirmation dialog for update
            setConfirmModal({
                visible: true,
                type: 'update',
                provider: selectedProvider,
                message: `Are you sure you want to update payment provider "${selectedProvider.name}"?`,
            });
            return;
        }
    };

    const confirmSave = async () => {
        if (!confirmModal.type || confirmModal.type !== 'update' || !confirmModal.provider) return;

        const trimmedCode = formState.code.trim().toLowerCase();
        const payload: PaymentProviderPayload = {
            code: trimmedCode,
            name: formState.name.trim(),
            description: formState.description.trim(),
            isActive: formState.isActive,
            credentials: (formState.credentials || [])
                .map((c) => ({
                    ...c,
                    name: c.name.trim(),
                    environment: c.environment || 'sandbox',
                    merchantId: c.merchantId.trim(),
                    serverKey: c.serverKey.trim(),
                    clientKey: c.clientKey.trim(),
                    additionalConfig: c.additionalConfig?.trim() || undefined,
                }))
                .filter((c) => c.name && c.environment && c.merchantId && c.serverKey && c.clientKey),
        };

        setSaving(true);
        try {
            await apiService.updatePaymentProvider(confirmModal.provider.id, payload);
            setConfirmModal({ visible: false, type: null, provider: null, message: '' });
            closeModal();
            fetchProviders();
        } catch (error: any) {
            console.error('Failed to save payment provider:', error);
            alert(error.message || 'Failed to save payment provider');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (provider: PaymentProvider) => {
        setConfirmModal({
            visible: true,
            type: 'delete',
            provider,
            message: `Are you sure you want to delete payment provider "${provider.name}"?\n\nNote: Providers used by transactions may not be deletable.`,
        });
    };

    const confirmDelete = async () => {
        if (!confirmModal.provider) return;
        
        try {
            await apiService.deletePaymentProvider(confirmModal.provider.id);
            setConfirmModal({ visible: false, type: null, provider: null, message: '' });
            fetchProviders();
        } catch (error: any) {
            console.error('Failed to delete payment provider:', error);
            alert(error.message || 'Failed to delete payment provider. Please try again.');
        }
    };

    const formatDateTime = (value: string) => {
        return new Date(value).toLocaleString();
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">Payment Provider Management</h1>
                </div>
                <div className="text-sm text-gray-500">Total Providers: {total}</div>
            </div>

            {/* Search & Filters */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center space-x-4">
                    <div className="flex-1">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search by code, name, or description..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Status:</label>
                        <select
                            value={isActiveFilter}
                            onChange={(e) => setIsActiveFilter(e.target.value as any)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                            <option value="all">All</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Page Size:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="bg-imola-primary hover:bg-imola-primary/80 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
                    >
                        <PlusIcon className="w-4 h-4" />
                        Add Provider
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Code
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Credentials
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Updated
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : providers.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                                        No payment providers found
                                    </td>
                                </tr>
                            ) : (
                                providers.map((provider) => (
                                    <tr key={provider.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {provider.code}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                            <div className="text-sm font-medium text-gray-900">{provider.name}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 max-w-sm text-left">
                                            <p className="line-clamp-2">{provider.description}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-left">
                                            {(provider.credentials || []).length} credential(s)
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                            <span
                                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${provider.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                    }`}
                                            >
                                                {provider.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                                            {formatDateTime(provider.updatedAt)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => openEditModal(provider)}
                                                    className="text-green-600 hover:text-green-900 p-1"
                                                    title="Edit"
                                                >
                                                    <PencilIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(provider)}
                                                    className="text-red-600 hover:text-red-900 p-1"
                                                    title="Delete"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                                disabled={currentPage >= Math.ceil(total / pageSize)}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                    <span className="font-medium">
                                        {Math.min(currentPage * pageSize, total)}
                                    </span>{' '}
                                    of <span className="font-medium">{total}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                                        disabled={currentPage >= Math.ceil(total / pageSize)}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            {modalVisible && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between border-b px-6 py-4">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">
                                    {modalMode === 'create' ? 'Create Payment Provider' : 'Edit Payment Provider'}
                                </h2>
                                <p className="text-sm text-gray-500">
                                    Configure provider metadata and API credentials.
                                </p>
                            </div>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                ✕
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formState.code}
                                        onChange={(e) => handleFormChange('code', e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                                        placeholder="e.g., midtrans, xendit, manual"
                                        required
                                        disabled={modalMode === 'edit'}
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Unique provider code (lowercase, cannot be changed after creation).
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={formState.name}
                                        onChange={(e) => handleFormChange('name', e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                                        required
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Description <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={formState.description}
                                    onChange={(e) => handleFormChange('description', e.target.value)}
                                    rows={3}
                                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-imola-primary focus:border-imola-primary"
                                    required
                                />
                            </div>
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700">Active</label>
                                <input
                                    type="checkbox"
                                    checked={formState.isActive ?? true}
                                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                                    className="rounded border-gray-300 text-imola-primary focus:ring-imola-primary"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Credentials <span className="text-gray-400 text-xs font-normal">(optional)</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={addCredentialRow}
                                        className="text-sm text-imola-primary hover:text-yellow-600 flex items-center gap-1"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        Add Credential
                                    </button>
                                </div>
                                <div className="space-y-3 max-h-80 overflow-y-auto border border-gray-200 rounded-md p-3">
                                    {formState.credentials && formState.credentials.length > 0 ? (
                                        formState.credentials.map((cred, index) => (
                                            <div
                                                key={index}
                                                className="border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2"
                                            >
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700">
                                                            Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={cred.name}
                                                            onChange={(e) =>
                                                                handleCredentialChange(index, 'name', e.target.value)
                                                            }
                                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary"
                                                            placeholder="e.g., Default Sandbox"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700">
                                                            Environment
                                                        </label>
                                                        <select
                                                            value={cred.environment || 'sandbox'}
                                                            onChange={(e) =>
                                                                handleCredentialChange(index, 'environment', e.target.value)
                                                            }
                                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary"
                                                        >
                                                            <option value="sandbox">sandbox</option>
                                                            <option value="production">production</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700">
                                                            Merchant ID
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={cred.merchantId}
                                                            onChange={(e) =>
                                                                handleCredentialChange(index, 'merchantId', e.target.value)
                                                            }
                                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-700">
                                                            Client Key
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={cred.clientKey}
                                                            onChange={(e) =>
                                                                handleCredentialChange(index, 'clientKey', e.target.value)
                                                            }
                                                            className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700">
                                                        Server Key
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={cred.serverKey}
                                                        onChange={(e) =>
                                                            handleCredentialChange(index, 'serverKey', e.target.value)
                                                        }
                                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:ring-imola-primary focus:border-imola-primary"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700">
                                                        Additional Config (JSON, optional)
                                                    </label>
                                                    <textarea
                                                        value={cred.additionalConfig || ''}
                                                        onChange={(e) =>
                                                            handleCredentialChange(index, 'additionalConfig', e.target.value)
                                                        }
                                                        rows={2}
                                                        className="mt-1 block w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-imola-primary focus:border-imola-primary"
                                                        placeholder='e.g. {"flag": true}'
                                                    />
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <label className="inline-flex items-center text-xs text-gray-700">
                                                        <input
                                                            type="checkbox"
                                                            checked={cred.isActive ?? true}
                                                            onChange={(e) =>
                                                                handleCredentialChange(index, 'isActive', e.target.checked)
                                                            }
                                                            className="rounded border-gray-300 text-imola-primary focus:ring-imola-primary mr-2"
                                                        />
                                                        Active
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCredentialRow(index)}
                                                        className="text-red-600 hover:text-red-800 text-xs"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-400 text-center py-4">
                                            No credentials configured. Click &quot;Add Credential&quot; to add one.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-4 py-2 bg-imola-primary text-white rounded-md text-sm font-medium hover:bg-imola-primary/80 disabled:opacity-50"
                                >
                                    {saving ? 'Saving...' : modalMode === 'create' ? 'Create' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal
                visible={confirmModal.visible}
                title={
                    confirmModal.type === 'update' ? 'Confirm Update' :
                    confirmModal.type === 'delete' ? 'Confirm Delete' : 'Confirm'
                }
                message={confirmModal.message}
                confirmText={
                    confirmModal.type === 'update' ? 'Update' :
                    confirmModal.type === 'delete' ? 'Delete' : 'Confirm'
                }
                cancelText="Cancel"
                confirmButtonClass={
                    confirmModal.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-imola-primary hover:bg-imola-primary/80'
                }
                onConfirm={() => {
                    if (confirmModal.type === 'delete') {
                        confirmDelete();
                    } else if (confirmModal.type === 'update') {
                        confirmSave();
                    }
                }}
                onCancel={() => setConfirmModal({ visible: false, type: null, provider: null, message: '' })}
            />
        </div>
    );
};

export default PaymentProviderManagement;


